import SentryService from '@services/SentryService';

import { AudioHealthMonitor } from './AudioHealthMonitor';
import { DeviceChangeCoordinator } from './DeviceChangeCoordinator';
import { DeviceEnumerator } from './DeviceEnumerator';
import { DevicePreferenceStore } from './DevicePreferenceStore';
import { MicLevelMeter } from './MicLevelMeter';
import { PermissionManager } from './PermissionManager';
import { StreamAcquirer } from './StreamAcquirer';
import type {
  AcquiredStream,
  AudioConstraints,
  AudioDeviceEventHandler,
  AudioDeviceEventMap,
  HealthState,
  NormalizedDevice,
  PermissionResult,
} from './types';

type EventListeners = {
  [K in keyof AudioDeviceEventMap]: Set<AudioDeviceEventHandler<K>>;
};

/**
 * Facade service that composes all audio device management sub-components.
 * Provides a single event-driven interface for device enumeration, acquisition,
 * health monitoring, and change detection.
 *
 * Usage:
 *   const service = new AudioInputDeviceService();
 *   const permission = await service.initialize();
 *   const stream = await service.acquire();
 *   service.on('healthChanged', ({ state }) => { ... });
 *   service.release();
 */
export class AudioInputDeviceService {
  private enumerator: DeviceEnumerator;
  private permissionManager: PermissionManager;
  private changeCoordinator: DeviceChangeCoordinator;
  private preferenceStore: DevicePreferenceStore;
  private streamAcquirer: StreamAcquirer;
  private healthMonitor: AudioHealthMonitor;
  private levelMeter: MicLevelMeter;
  /** Shared between the health monitor and level meter to limit concurrent AudioContexts. */
  private sharedAudioContext: AudioContext | null = null;

  private activeStream: AcquiredStream | null = null;
  private initialized = false;

  private listeners: EventListeners = {
    devicesChanged: new Set(),
    healthChanged: new Set(),
    perfSample: new Set(),
    performanceSummary: new Set(),
    permissionChanged: new Set(),
    deviceLost: new Set(),
  };

  constructor() {
    this.enumerator = new DeviceEnumerator();
    this.permissionManager = new PermissionManager();
    this.changeCoordinator = new DeviceChangeCoordinator(this.enumerator);
    this.preferenceStore = new DevicePreferenceStore();
    this.streamAcquirer = new StreamAcquirer(this.enumerator);
    this.healthMonitor = new AudioHealthMonitor();
    this.levelMeter = new MicLevelMeter();

    // Wire up internal event forwarding
    this.changeCoordinator.setOnDevicesChanged(diff => {
      this.emit('devicesChanged', diff);
    });

    this.changeCoordinator.setOnDeviceLost(device => {
      this.emit('deviceLost', device);
    });

    this.healthMonitor.setOnHealthChanged((state, prev) => {
      this.emit('healthChanged', { state, prev });
    });

    this.healthMonitor.setOnPerfSample(perf => {
      this.emit('perfSample', perf);
    });
  }

  // -- Lifecycle --

  /**
   * Initialize the service: request permission, enumerate devices,
   * and start change detection.
   */
  async initialize(_userId?: string): Promise<PermissionResult> {
    SentryService.addBreadcrumb({
      category: 'audio.service',
      message: 'Initializing AudioInputDeviceService',
      level: 'info',
    });

    // Request permission
    const permission = await this.permissionManager.requestPermission();
    this.emit('permissionChanged', permission);

    if (!permission.granted) {
      SentryService.addBreadcrumb({
        category: 'audio.service',
        message: `Permission denied: ${permission.error}`,
        level: 'warning',
      });
      return permission;
    }

    // Enumerate devices
    await this.enumerator.enumerate();

    // Start change detection in idle mode
    this.changeCoordinator.start('idle');

    this.initialized = true;

    SentryService.addBreadcrumb({
      category: 'audio.service',
      message: `Initialized with ${this.enumerator.getCachedDevices().length} devices`,
      level: 'info',
    });

    return permission;
  }

  /**
   * Initialize the service without triggering a getUserMedia prompt.
   * Uses navigator.permissions.query() to check existing permission state.
   * Suitable for return visits where permission was previously granted.
   * Falls back gracefully (returns not-granted) on browsers that don't
   * support permissions.query for microphone (e.g. Safari).
   */
  async initializeSilent(): Promise<PermissionResult> {
    if (this.initialized) return this.permissionManager.getState();

    const permission = await this.permissionManager.checkPermission();

    if (!permission.granted) {
      return permission;
    }

    // Permission confirmed — enumerate devices (labels available without active stream)
    await this.enumerator.enumerate();
    this.changeCoordinator.start('idle');
    this.initialized = true;

    SentryService.addBreadcrumb({
      category: 'audio.service',
      message: `Silent init: ${this.enumerator.getCachedDevices().length} devices`,
      level: 'info',
    });

    return permission;
  }

  /**
   * Acquire a stream for the given device (by internalId).
   * If no deviceId is provided, uses the stored preference or system default.
   */
  private acquiring = false;

  async acquire(
    deviceInternalId?: string,
    options?: AudioConstraints,
  ): Promise<AcquiredStream> {
    if (this.acquiring) {
      throw new Error('acquire() already in progress');
    }
    this.acquiring = true;

    // Release any existing stream first
    this.release();

    try {
      // Resolve the browser device ID
      let browserDeviceId: string | undefined;

      if (deviceInternalId) {
        const device = this.enumerator.findByInternalId(deviceInternalId);
        browserDeviceId = device?.browserDeviceId;
      }

      // If no explicit device, try stored preference
      if (!browserDeviceId) {
        const pref = this.preferenceStore.load();
        if (pref) {
          const match = this.preferenceStore.findBestMatch(pref, this.enumerator.getCachedDevices());
          browserDeviceId = match?.browserDeviceId;
        }
      }

      SentryService.addBreadcrumb({
        category: 'audio.service',
        message: 'Acquiring stream',
        data: { deviceInternalId, browserDeviceId },
        level: 'info',
      });

      // Acquire the stream
      const result = await this.streamAcquirer.acquire(browserDeviceId, options);
      this.activeStream = result;

      // Save the preference for next time
      this.preferenceStore.save(result.device);

      // Start the warning track + honest level meter on one shared AudioContext.
      this.startMonitors(result.stream);

      // Switch change coordinator to recording mode
      this.changeCoordinator.setMode('recording');

      // Observe the track for ended events
      const track = result.stream.getAudioTracks()[0];
      if (track) {
        this.changeCoordinator.observeTrack(track);
      }

      return result;
    } finally {
      this.acquiring = false;
    }
  }

  /** Release the current stream and stop monitoring */
  release(): void {
    if (this.activeStream) {
      // Emit performance summary before stopping
      const summary = this.healthMonitor.getPerformanceSummary();
      this.emit('performanceSummary', { ...summary, device_count: this.getDevices().length });

      // Stop both monitors and close their shared AudioContext
      this.stopMonitors();

      // Stop all tracks
      this.activeStream.stream.getTracks().forEach(t => t.stop());

      SentryService.addBreadcrumb({
        category: 'audio.service',
        message: 'Stream released',
        data: { deviceId: this.activeStream.device.browserDeviceId },
        level: 'info',
      });

      this.activeStream = null;

      // Switch back to idle polling
      this.changeCoordinator.setMode('idle');
    }
  }

  // -- Health monitoring (can be used independently of acquire) --

  /**
   * Set the callback for audio level updates (~50ms interval, normalized 0-100).
   * Driven by the decoupled {@link MicLevelMeter} (honest, time-domain RMS),
   * NOT the health monitor's dB-frequency level.
   */
  setOnLevelChanged(handler: ((level: number) => void) | null): void {
    this.levelMeter.setOnLevelChanged(handler);
  }

  /** Start health monitoring + level metering on a stream (e.g. mic test) */
  startMonitoring(stream: MediaStream, audioContext?: AudioContext): void {
    this.startMonitors(stream, audioContext);
  }

  /** Stop health monitoring + level metering */
  stopMonitoring(): void {
    this.stopMonitors();
  }

  /**
   * Start the health monitor + level meter on a SINGLE AudioContext.
   *
   * Sharing one context keeps us well under the browser's ~6-context-per-page
   * limit (the recorder and audio mixer also create contexts during a session).
   * Both are monitoring/visualization only — never the capture path — so neither
   * is allowed to block a recording: if the context can't be created (almost
   * always "too many AudioContexts") or a monitor fails to start, we capture a
   * Sentry error and carry on. The raw recording stream is unaffected.
   */
  private startMonitors(stream: MediaStream, externalAudioContext?: AudioContext): void {
    let audioContext = externalAudioContext;
    if (!audioContext) {
      try {
        audioContext = new AudioContext();
        this.sharedAudioContext = audioContext;
      } catch (err) {
        SentryService.addBreadcrumb({
          category: 'audio.service',
          message:
            'AudioContext creation failed — likely too many concurrent AudioContexts; skipping monitors (recording continues)',
          level: 'error',
        });
        SentryService.captureException(err instanceof Error ? err : new Error(String(err)));
        return;
      }
    }

    try {
      this.healthMonitor.start(stream, audioContext);
    } catch (err) {
      SentryService.addBreadcrumb({
        category: 'audio.service',
        message: 'AudioHealthMonitor failed to start (non-critical, monitoring only)',
        level: 'warning',
      });
      SentryService.captureException(err instanceof Error ? err : new Error(String(err)));
    }

    try {
      this.levelMeter.start(stream, audioContext);
    } catch (err) {
      SentryService.addBreadcrumb({
        category: 'audio.service',
        message: 'MicLevelMeter failed to start (non-critical, waveform only)',
        level: 'warning',
      });
      SentryService.captureException(err instanceof Error ? err : new Error(String(err)));
    }
  }

  /** Stop both monitors and close the shared AudioContext (if we own it). */
  private stopMonitors(): void {
    this.healthMonitor.stop();
    this.levelMeter.stop();
    if (this.sharedAudioContext) {
      if (this.sharedAudioContext.state !== 'closed') {
        this.sharedAudioContext.close().catch(() => {});
      }
      this.sharedAudioContext = null;
    }
  }

  // -- Getters --

  /** Get the current list of normalized devices */
  getDevices(): NormalizedDevice[] {
    return this.enumerator.getCachedDevices();
  }

  /** Get the currently active stream */
  getActiveStream(): AcquiredStream | null {
    return this.activeStream;
  }

  /** Get the current health state */
  getHealthState(): HealthState {
    return this.healthMonitor.getState();
  }

  /** Get the current permission state */
  getPermissionState(): PermissionResult {
    return this.permissionManager.getState();
  }

  /** Check if the service has been initialized */
  isInitialized(): boolean {
    return this.initialized;
  }

  /** Get the preference store (for bluetooth warning suppression, etc.) */
  getPreferenceStore(): DevicePreferenceStore {
    return this.preferenceStore;
  }

  // -- Events --

  /** Subscribe to an event */
  on<K extends keyof AudioDeviceEventMap>(
    event: K,
    handler: AudioDeviceEventHandler<K>,
  ): void {
    (this.listeners[event] as Set<AudioDeviceEventHandler<K>>).add(handler);
  }

  /** Unsubscribe from an event */
  off<K extends keyof AudioDeviceEventMap>(
    event: K,
    handler: AudioDeviceEventHandler<K>,
  ): void {
    (this.listeners[event] as Set<AudioDeviceEventHandler<K>>).delete(handler);
  }

  /** Emit an event to all subscribers */
  private emit<K extends keyof AudioDeviceEventMap>(
    event: K,
    data: AudioDeviceEventMap[K],
  ): void {
    for (const handler of this.listeners[event]) {
      try {
        handler(data);
      } catch (error) {
        SentryService.captureException(error instanceof Error ? error : new Error(String(error)));
      }
    }
  }

  /** Full cleanup — stop everything and remove all listeners */
  dispose(): void {
    this.release();
    this.changeCoordinator.stop();
    this.stopMonitors();

    // Clear all listeners
    for (const set of Object.values(this.listeners)) {
      set.clear();
    }

    this.initialized = false;

    SentryService.addBreadcrumb({
      category: 'audio.service',
      message: 'AudioInputDeviceService disposed',
      level: 'info',
    });
  }
}
